import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  useIonViewWillEnter,
  useIonViewWillLeave,
  useIonModal,
  IonFooter,
  IonButton,
  IonButtons,
  IonBackButton,
  IonIcon,
  IonBadge,
  IonSpinner,
} from "@ionic/react";
import React from "react";
import { AppContext } from "../components/AppContext";
import { useContext } from "react";
import { VideoConverterProps } from "../interfaces/core";
import VideoConverterSettings from "../components/VideoConverter/VideoConverterSettings";
import VideoConverterComponent from "../components/VideoConverter/VideoConverterComponent";
import { images, imagesSharp } from "ionicons/icons";
import { useEffect } from "react";
import {
  getSettings,
  resetDefaultSettings,
  updateSettings,
} from "../lib/Store";
import { possibleEmoji } from "../lib/video-converter/emojiLib";
import { takeImageSnapshot } from "../lib/saveCanvas";
import VideoConverterGallery from "../components/VideoConverter/VideoConverterGallery";
import useStateRef from "react-usestateref";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const defaultSettings: VideoConverterProps = {
  audio: {
    enabled: false,
  },
  display: {
    dpi: 48,
    background: { h: 236, s: 100, l: 100 },
  },
  pixel: {
    type: "emoji",
    options: {
      source: possibleEmoji,
      displayMode: "fixed",
    },
  },
};
const VideoConverter: React.FC = () => {
  const settingsKey = "video-converter-settings";
  const galleryKey = "video-converter-storage";
  const appContext = useContext(AppContext);
  const { setTabBarHidden } = appContext.tabBar;
  const [settings, setSettings, settingsRef] = useStateRef(defaultSettings);
  const [gallery, setGallery, galleryRef] = useStateRef([]);
  const [canvas, setCanvas] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [flash, setFlash] = useState("");
  const { t } = useTranslation();

  useIonViewWillEnter(() => setTabBarHidden(true));
  useIonViewWillLeave(() => {
    setTabBarHidden(false);
  });

  const [presentVideoSettings, dismissVideoSettings] = useIonModal(
    VideoConverterSettings,
    {
      settings,
      handleProjectStore: (value) =>
        updateSettings({
          store: appContext?.store,
          key: settingsKey,
          value,
          setValue: setSettings,
        }),
      handleResetDefaultSettings: () =>
        resetDefaultSettings({
          store: appContext?.store,
          key: settingsKey,
          defaultSettings,
          setValue: setSettings,
        }),
      handleDismiss: () => dismissVideoSettings(),
    }
  );
  const [presentGallery, dismissGallery] = useIonModal(VideoConverterGallery, {
    gallery: galleryRef.current,
    handleProjectStore: (value) =>
      updateSettings({
        store: appContext?.store,
        key: galleryKey,
        value,
        setValue: (value) => setGallery(value),
      }),
    handleDismiss: () => dismissGallery(),
  });
  const getCanvas = (canvas) => {
    setCanvas(canvas);
  };
  const takePic = async () => {
    setIsLoading(true);
    setFlash("bg-white dark:bg-black transition-none");
    setTimeout(() => {
      setFlash("transition-all duration-500");
      setTimeout(() => {
        setFlash("bg-transparent");
      }, 200);
    }, 100);
    await takeImageSnapshot(
      canvas.canvas.canvas,
      `image-${new Date().valueOf()}`,
      0.85,
      {
        dpi: 2,
        onExport: async (blob, filename) =>
          await updateSettings({
            store: appContext?.store,
            key: galleryKey,
            value: [...gallery, { blob, filename, checked: false }],
            setValue: (value) => setGallery(value),
          }).then(() => {
            setIsLoading(false);
          }),
      }
    );
  };
  const getProjectSettings = async () => {
    getSettings({
      store: appContext?.store,
      key: settingsKey,
      setValue: (value) => value && setSettings(value),
    });
    getSettings({
      store: appContext?.store,
      key: galleryKey,
      setValue: (value) => value && setGallery(value),
    });
  };
  useEffect(() => {
    if (typeof appContext.store !== "undefined") {
      getProjectSettings();
    }
  }, [appContext.store]);
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              onClick={(e) => {
                presentGallery({
                  swipeToClose: true,
                  onWillPresent: () => canvas.p5.noLoop(),
                  onDidDismiss: () => canvas.p5.loop(),
                });
              }}
            >
              <IonIcon slot="start" ios={images} md={imagesSharp} />
              {gallery.length > 0 && (
                <IonBadge slot="end">{gallery.length}</IonBadge>
              )}
            </IonButton>
          </IonButtons>
          <IonTitle>{t(`Video Converter.title`)}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div
          className={`w-full flex justify-center relative h-full items-center`}
        >
          <div
            className={`pointer-events-none w-full h-full absolute z-10 ${flash}`}
          />
          <VideoConverterComponent
            settings={settingsRef.current as VideoConverterProps}
            getCanvas={getCanvas}
          />
        </div>
      </IonContent>
      <IonFooter>
        <IonToolbar className="!px-0">
          <div className="flex gap-2 px-2 max-w-7xl mx-auto justify-between">
            <IonButton
              //   disabled={disabled}
              className="font-semibold max-w-xs w-full"
              expand="block"
              onClick={() => {
                presentVideoSettings({
                  swipeToClose: true,
                  onWillPresent: () => canvas.p5.noLoop(),
                  onDidDismiss: () => canvas.p5.loop(),
                });
              }}
              color="secondary"
            >
              {t(`Customize...`)}
            </IonButton>
            <IonButton
              disabled={isLoading}
              color="primary"
              onClick={takePic}
              className="font-semibold max-w-xs w-full"
              expand="block"
            >
              {isLoading ? <IonSpinner /> : "Capture"}
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default VideoConverter;
